import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  addFamilyDependant,
  deleteFamilyDependant,
  getFamilyDashboard,
  getFamilyMemberVaccinationById,
  getFamilyMembers,
  getUpcomingVaccinations,
  updateFamilyDependant,
} from "service";
import { toast } from "sonner";
import { useModal } from "./useModal";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "./useUser";
import { IMyVaccinationResponse } from "types";
import {
  FamilyMember,
  IAddDependdantForm,
  ApiResponse,
  TransformedResponse,
  User,
} from "types";

export const useFamilyAccounts = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams();

  const { user } = useUser();
  const { closeModal, isOpen, openModal } = useModal();

  const [dependent, setDependent] = useState<FamilyMember | null>(null);
  const [formData, setFormData] = useState<IAddDependdantForm>({
    address: "",
    gender: "",
    history_of_allergies: "",
    dob: "",
    surname: "",
    firstname: "",
    other_names: "",
    relationship: "",
  });

  const {
    data: familyAccounts,
    isLoading: familyAccountsLoading,
    isError: familyAccountsError,
  } = useSuspenseQuery<ApiResponse>({
    queryKey: ["family-accounts"],
    queryFn: async () => {
      const response = await getFamilyMembers();
      return response;
    },
  });

  useEffect(() => {
    if (!params.id && !familyAccounts) return;
    const user = familyAccounts.results.find(
      (account) => account.user.ino_id === params.id
    );

    if (user) {
      setDependent(user);
    }
  }, [familyAccounts, params.id]);

  const { data: familyMembervaccines, isPending: familyMembervaccinesLoading } =
    useQuery<IMyVaccinationResponse>({
      queryKey: ["family-member-vaccines", dependent?.id],
      enabled: !!dependent,
      // initialPageParam: 1,
      // getNextPageParam: (lastPage) => {
      //   if (!lastPage?.next) return undefined;
      //   const url = new URL(lastPage.next);
      //   return Number(url.searchParams.get("page")) || undefined;
      // },
      // getPreviousPageParam: (_, allPages) => {
      //   const firstLoadedPage = allPages[allPages.length - 1];
      //   if (!firstLoadedPage?.previous) return undefined;
      //   try {
      //     const url = new URL(firstLoadedPage.previous);
      //     const pageParam = Number(url.searchParams.get("page"));
      //     return isNaN(pageParam) ? undefined : pageParam;
      //   } catch (error) {
      //     console.error("Error parsing previous page URL:", error);
      //     return undefined;
      //   }
      // },
      queryFn: async () => {
        if (!dependent) return;

        const response = getFamilyMemberVaccinationById(dependent.id);
        return response;
      },
    });

  const {
    mutate: addFamilyDependantMutation,
    isPending: addFamilyDependantIsPending,
  } = useMutation({
    mutationFn: async () => {
      const response = await addFamilyDependant(formData);
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["family-accounts"],
      });
      setFormData({
        address: "",
        gender: "",
        history_of_allergies: "",
        dob: "",
        surname: "",
        firstname: "",
        other_names: "",
        relationship: "",
      });
      toast.success("Dependant added successfully");
      closeModal();
    },
    onError: (error: any) => {
      const errors = error.response?.data.errors;

      if (Array.isArray(errors)) {
        const errorMessages = errors.map((err) => err.detail).join(", ");
        toast.error(errorMessages);
      } else {
        toast.error("An unknown error occurred.");
      }
    },
  });

  // delete
  const {
    mutate: deleteFamilyDependantMutation,
    isPending: deleteFamilyDependantIsPending,
  } = useMutation({
    mutationFn: async (accountId: number) => {
      const response = await deleteFamilyDependant(accountId);
      return response;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["family-accounts"],
      });
    },
  });

  const {
    mutate: updateFamilyDependantMutation,
    isPending: updateFamilyDependantIsPending,
  } = useMutation({
    mutationFn: async (accountId: number) => {
      const response = await updateFamilyDependant(accountId);
      return response;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["family-accounts"],
      });
    },
  });

  // get family dashboard
  const { data: familyDashboard, isPending: isFamilyDashboardLoading } =
    useQuery({
      queryKey: ["family-dashboard"],
      queryFn: async () => {
        if (!dependent) return;
        const response = await getFamilyDashboard(dependent?.id);
        return response;
      },
      enabled: !!dependent,
    });

  const handleCreate = () => {
    addFamilyDependantMutation();
  };

  const handleDelete = (accountId: number) => {
    deleteFamilyDependantMutation(accountId);
  };

  const handleUpdate = (accountId: number) => {
    updateFamilyDependantMutation(accountId);
  };

  const handleFieldChange = (
    field: keyof IAddDependdantForm,
    value: string | number | boolean
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const isFormComplete = () => {
    return (
      formData.address &&
      formData.gender &&
      formData.history_of_allergies &&
      formData.dob &&
      formData.surname &&
      formData.firstname &&
      formData.other_names &&
      formData.relationship
    );
  };

  function transformResponse(data: ApiResponse): TransformedResponse {
    if (data.results.length === 0)
      return { ...data, primary_user: {} as User, results: [] };

    const primaryUser = data.results[0].family.primary_user;

    const uniqueResults = data.results.map(({ family, ...rest }) => rest);

    return {
      count: data.count,
      next: data.next,
      previous: data.previous,
      primary_user: primaryUser,
      results: uniqueResults,
    };
  }

  const familyAccountsData: TransformedResponse =
    transformResponse(familyAccounts);

  const loading =
    familyAccountsLoading ||
    addFamilyDependantIsPending ||
    deleteFamilyDependantIsPending ||
    updateFamilyDependantIsPending;

  return {
    familyAccountsData,
    isLoading: familyAccountsLoading || addFamilyDependantIsPending,
    error: familyAccountsError,
    formData,
    isOpen,
    user,
    dependent,
    familyMembervaccines,
    familyMembervaccinesLoading,
    openModal,
    closeModal,
    handleCreate,
    handleFieldChange,
    isFormComplete,
    navigate,
    handleDelete,
    handleUpdate,
    familyDashboard,
    isFamilyDashboardLoading,
  };
};
